import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useHolidays } from './useHolidays';
import {
  fetchTeamHolidayOverrides,
  setTeamHolidayOverrides,
} from '@/services/teamHolidays';
import { mergeTeamHolidayOverrides } from '@/utils/attendanceUtils';
import { queryKeys } from '@/config/queryKeys';

/**
 * selectedTeamId = null   -> company defaults: `holidays` is just the
 *                             company's own list, unmodified.
 * selectedTeamId = string -> that team's effective list: the company list
 *                             with the team's own per-date overrides applied
 *                             on top (an override is either the team's own
 *                             version of that date, or `null` meaning the
 *                             team doesn't observe it). Overrides persist
 *                             independently of later company-side edits or
 *                             deletes to that date - the team owns them.
 */
export function useEffectiveHolidays(selectedTeamId) {
  const year = new Date().getFullYear();
  const queryClient = useQueryClient();

  const { holidays: companyHolidays, loading: isLoadingCompany } =
    useHolidays();

  const { data: overrides = {}, isLoading: isLoadingTeam } = useQuery({
    queryKey: queryKeys.holidays.teamOverrides(selectedTeamId, year),
    queryFn: () => fetchTeamHolidayOverrides(selectedTeamId, year),
    enabled: !!selectedTeamId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const holidays = useMemo(
    () =>
      selectedTeamId
        ? mergeTeamHolidayOverrides(companyHolidays, overrides)
        : companyHolidays,
    [companyHolidays, overrides, selectedTeamId]
  );

  const setOverridesMutation = useMutation(
    ({ updates, updatedBy }) =>
      setTeamHolidayOverrides({
        teamId: selectedTeamId,
        year,
        updates,
        updatedBy,
      }),
    {
      onSuccess: () =>
        queryClient.invalidateQueries(
          queryKeys.holidays.teamOverrides(selectedTeamId, year)
        ),
    }
  );

  // Batch form: one write for however many dates an operation touches (e.g.
  // moving a holiday to a new date clears the old one and claims the new
  // one together).
  const setOverrides = useCallback(
    (updates, updatedBy) =>
      setOverridesMutation.mutateAsync({ updates, updatedBy }),
    [setOverridesMutation]
  );

  const setOverride = useCallback(
    (date, holiday, updatedBy) => setOverrides({ [date]: holiday }, updatedBy),
    [setOverrides]
  );

  return {
    holidays,
    isLoading: isLoadingCompany || (!!selectedTeamId && isLoadingTeam),
    isSaving: setOverridesMutation.isLoading,
    setOverride,
    setOverrides,
  };
}
